import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ShoppingCart, Eye } from "lucide-react";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { toast } from "sonner";

const ProductCard = ({ product, addToCart, index = 0 }) => {
  const handleAddToCart = (e) => {
    e.preventDefault();
    e.stopPropagation();
    addToCart(product.id);
    toast.success(`${product.name} added to cart`, {
      description: "View your cart to checkout",
    });
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.1 }}
      whileHover={{ y: -5 }}
      className="group"
    >
      <Link
        to={`/product/${product.id}`}
        data-testid={`product-card-${product.id}`}
        className="block"
      >
        <div className="relative bg-card rounded-xl overflow-hidden border border-border hover:border-primary/50 transition-all duration-300">
          {/* Image */}
          <div className="relative aspect-square overflow-hidden bg-muted">
            <img
              src={product.image_url}
              alt={product.name}
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
            />
            
            {/* Overlay */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
            
            {/* Quick Actions */}
            <div className="absolute bottom-4 left-4 right-4 flex gap-2 opacity-0 group-hover:opacity-100 transition-all duration-300 transform translate-y-4 group-hover:translate-y-0">
              <Button
                data-testid={`quick-add-${product.id}`}
                onClick={handleAddToCart}
                className="flex-1 bg-primary text-primary-foreground hover:bg-primary/90 font-outfit text-sm"
              >
                <ShoppingCart className="w-4 h-4 mr-2" />
                Add to Cart
              </Button>
              <Button
                data-testid={`quick-view-${product.id}`}
                variant="outline"
                size="icon"
                className="border-white/20 bg-white/10 hover:bg-white/20"
              >
                <Eye className="w-4 h-4" />
              </Button>
            </div>

            {/* Featured Badge */}
            {product.featured && (
              <Badge className="absolute top-3 left-3 bg-accent text-accent-foreground">
                Featured
              </Badge>
            )}

            {/* Low Stock Badge */}
            {product.stock <= 5 && product.stock > 0 && (
              <Badge className="absolute top-3 right-3 bg-secondary text-secondary-foreground">
                Low Stock
              </Badge>
            )}
          </div>

          {/* Content */}
          <div className="p-4">
            <div className="mb-2">
              <span className="text-xs font-outfit text-primary uppercase tracking-wider">
                {product.category.replace("-", " ")}
              </span>
            </div>
            <h3 className="font-unbounded text-sm font-medium text-foreground mb-2 line-clamp-1">
              {product.name}
            </h3>
            <p className="font-outfit text-xs text-muted-foreground mb-3 line-clamp-2">
              {product.description}
            </p>
            <div className="flex items-center justify-between">
              <span className="font-unbounded text-lg font-bold text-primary">
                ${product.price.toFixed(2)}
              </span>
              {product.stock > 0 ? (
                <span className="text-xs text-green-500 font-outfit">In Stock</span>
              ) : (
                <span className="text-xs text-accent font-outfit">Out of Stock</span>
              )}
            </div>
          </div>
        </div>
      </Link>
    </motion.div>
  );
};

export default ProductCard;
